import api from '@/services/api'
import { useConfig } from '@/composables/game/config'
import { type FortuneWheelPrizeV2, type FortuneWheelSlice, type PlayerFortuneWheelState } from '@/types/shared/fortune-wheel'
import type { PlayerStats } from '@/types/shared/player'
import { computed, ref } from 'vue'
import { useLocalStorage } from '@vueuse/core'
import { buildDropeeStorageKey } from '@/config/config'
import { thisOccurrence } from '@/helpers/date'
import { useNow } from '@/composables/game/time'
import { notifySpinForUtmSource, syncPlayer, usePlayer } from '@/composables/game/player'
import { useOnboarding } from '@/composables/game/onboarding'
import { sleep } from '@/services/helper'

export class SpinFailed extends Error {
}

export const MULTIPLIER_MIN_SPINS = 1

const MULTIPLIER_TIERS: [number, number[]][] = [
  [10000, [1, 10, 100, 500, 1000]],
  [1000, [1, 10, 100, 500]],
  [100, [1, 10, 100]],
  [10, [1, 2, 5, 10]],
  [0, [1, 2]],
]

export function getMultiplierOptions(availableSpins: number): number[] {
  for (const [threshold, options] of MULTIPLIER_TIERS) {
    if (availableSpins >= threshold) {
      return options
    }
  }
  return [1]
}

export function getHighestAffordableMultiplier(availableSpins: number): number {
  const options = getMultiplierOptions(availableSpins)
  const affordable = options.filter(m => availableSpins >= m)
  return affordable.length > 0 ? Math.max(...affordable) : 1
}

// Module-level slice state shared across all composable instances
const wheelSlicesState = ref<FortuneWheelSlice[] | null>(null)
const goldenWheelSlicesState = ref<FortuneWheelSlice[] | null>(null)

export const useFortuneWheel = () => {
  const { config } = useConfig()
  const { isOnboardingDone, doOnboarding } = useOnboarding()

  const AUTO_SPIN_LIMIT = 10
  const ONBOARDING_LIMIT = 1

  const availableSpins = computed(() => fortuneWheelState.value.spins.available)
  const isAutoSpin = ref<boolean>(false)
  const canAutoSpin = computed(() => availableSpins.value > 0)
  const isAutoSpinOnHold = ref<boolean>(false)
  const { dropeeBalanceCurrent } = usePlayer()

  const fortuneWheelState = useLocalStorage<PlayerFortuneWheelState>(buildDropeeStorageKey('fortuneWheelState'), {
    usdtCentsBalance: 0,
    dropeeCentsBalance: 0,
    dropeeCentsBalanceAllTime: 0,
    usdtCentsBalanceAllTime: 0,
    spins: {
      total: 0,
      available: 0,
      lastDailyTicketTimestamp: 0,
      maxStreak: 0,
      streak: 0,
      lastSpinDate: '',
    },
    cards: {},
    lastWonPrizes: [],
    withdrawals: [],
  } satisfies PlayerFortuneWheelState)

  // Slices: use updated state from spin response, fall back to config
  const wheelSlices = computed<FortuneWheelSlice[]>(() =>
    wheelSlicesState.value ?? config.value.game.fortuneWheel.ui?.slices ?? [],
  )
  const goldenWheelSlices = computed<FortuneWheelSlice[]>(() =>
    goldenWheelSlicesState.value ?? config.value.game.fortuneWheel.ui?.goldenSlices ?? [],
  )

  const lasWonPrizesCount = computed(() => fortuneWheelState.value.lastWonPrizes.length)

  async function checkOnboardingState() {
    if (lasWonPrizesCount.value >= ONBOARDING_LIMIT && !isOnboardingDone.value) {
      await doOnboarding()
    }
  }

  async function updateState(state?: PlayerFortuneWheelState) {
    const result = state ? { state } : await api.getFortuneWheelState()
    fortuneWheelState.value = result.state
  }

  async function spin({ multiplier }: { multiplier: number }): Promise<{
    prize: FortuneWheelPrizeV2,
    playerStats: PlayerStats,
    fortuneWheelPlayerState: PlayerFortuneWheelState
    prizeMultiplier: number
  } | null> {
    for (let i = 0; i < 5; i++) {
      try {
        const result = await api.spinV2({ multiplier })
        if (!result.prize) {
          continue
        }
        fortuneWheelState.value = result.fortuneWheelPlayerState
        if (result.gameConfig.fortuneWheel.ui) {
          wheelSlicesState.value = result.gameConfig.fortuneWheel.ui.slices
          goldenWheelSlicesState.value = result.gameConfig.fortuneWheel.ui.goldenSlices
        }
        if (notifySpinForUtmSource.value) {
          api.notifySpin(notifySpinForUtmSource.value).then()
          notifySpinForUtmSource.value = ''
        }
        return result
      } catch (e) {
        await sleep(500)
        await syncPlayer()
        await updateState()
      }
    }
    return null
  }

  function updateAutoSpin(value: boolean) {
    isAutoSpin.value = value
  }

  return {
    spin: spin,
    updateState: updateState,
    fortuneWheelState,
    availableSpins,
    wheelSlices,
    goldenWheelSlices,
    currentBalance: computed(() => fortuneWheelState.value.dropeeCentsBalance / 100 + dropeeBalanceCurrent.value),
    isAutoSpin,
    canAutoSpin,
    isAutoSpinOnHold,
    updateAutoSpin,
    AUTO_SPIN_LIMIT,
    checkOnboardingState,
    lasWonPrizesCount,
  }
}

export const useGoldWheel = () => {
  const { fortuneWheelState } = useFortuneWheel()
  const { config } = useConfig()
  const { nowDateTimestamp } = useNow()

  const todayDate = computed(() => thisOccurrence(0, nowDateTimestamp.value).toISODate())
  const isTodayClaimed = computed(() => fortuneWheelState.value.spins.lastSpinDate === todayDate.value)
  const completedStreakCount = computed(() => fortuneWheelState.value.spins.streak)
  const streak = computed<{ index: number, active: boolean, completed: boolean }[]>(() => {
    return [...Array(7).keys()].map((element, index) => {
      return {
        index: index + 1,
        active: completedStreakCount.value === index,
        completed: completedStreakCount.value > index,
      }
    })
  })

  const isGold = computed<boolean>(() => fortuneWheelState.value.spins.streak === config.value.game.fortuneWheel.goldenWheel.activation.afterDays)

  return {
    streak,
    isGold,
    isTodayClaimed,
    completedStreakCount,
  }
}
